import type { ReadingPosition } from './useBookUrl'
import type { TocItemData } from '~/utils/literature/hierarchy-builder'
import type { Page } from '~/utils/solr/literature'

interface PageLoaderLike {
  pages: Readonly<Ref<Page[]>>
  isFetching: Readonly<Ref<boolean>>
}

interface UseBookTocFallbackNavigationOptions {
  flatTocItems: Ref<TocItemData[]>
  pageLoader: PageLoaderLike
  updateUrl: (newPosition: ReadingPosition) => void
  onUnresolvedPageNumber?: (node: TocItemData) => void
}

export function useBookTocFallbackNavigation(options: UseBookTocFallbackNavigationOptions) {
  const {
    flatTocItems,
    pageLoader,
    updateUrl,
    onUnresolvedPageNumber,
  } = options

  function getChapterNumber(href?: string): number | null {
    if (!href) {
      return null
    }

    const chapterMatch = href.match(/chapter(\d+)\.xhtml/i)
    if (!chapterMatch?.[1]) {
      return null
    }

    const chapterNumber = Number.parseInt(chapterMatch[1], 10)
    return Number.isFinite(chapterNumber) ? chapterNumber : null
  }

  function resolveChapterStartPage(node: TocItemData): string | undefined {
    const normalizedHref = node.href?.split('#')[0]
    if (!normalizedHref) {
      return undefined
    }

    const chapterStart = flatTocItems.value.find(item =>
      !item.fragmentId
      && !!item.pageNumberString
      && item.href === normalizedHref,
    )

    return chapterStart?.pageNumberString
  }

  function resolveNextChapterPage(node: TocItemData): string | undefined {
    const currentChapter = getChapterNumber(node.href)
    if (currentChapter === null) {
      return undefined
    }

    let nextChapterPage: { chapter: number, pageNumber: string } | null = null

    for (const item of flatTocItems.value) {
      if (!item.pageNumberString) {
        continue
      }

      const chapter = getChapterNumber(item.href)
      if (chapter === null || chapter <= currentChapter) {
        continue
      }

      if (!nextChapterPage || chapter < nextChapterPage.chapter) {
        nextChapterPage = {
          chapter,
          pageNumber: item.pageNumberString,
        }
      }
    }

    return nextChapterPage?.pageNumber
  }

  function isPageLoaded(pageNumber: string): boolean {
    return pageLoader.pages.value.some(page => page.pageNumberString === pageNumber)
  }

  function isTocNodePresentInLoadedContent(node: TocItemData): boolean {
    const anchor = node.fragmentId
    if (anchor) {
      const anchorPatterns = [`id="${anchor}"`, `id='${anchor}'`]
      return pageLoader.pages.value.some((page) => {
        const bodytext = page.bodytext
        if (!bodytext) {
          return false
        }
        return anchorPatterns.some(pattern => bodytext.includes(pattern))
      })
    }

    if (node.pageNumberString) {
      return isPageLoaded(node.pageNumberString)
    }

    return false
  }

  async function waitForFetchToSettle(timeoutMs = 4000): Promise<void> {
    const start = Date.now()
    while (Date.now() - start < timeoutMs) {
      if (!pageLoader.isFetching.value) {
        return
      }
      await new Promise(resolve => requestAnimationFrame(resolve))
    }
  }

  async function waitForPageToLoad(pageNumber: string, timeoutMs = 6000): Promise<void> {
    const start = Date.now()
    while (Date.now() - start < timeoutMs) {
      if (isPageLoaded(pageNumber)) {
        return
      }
      await new Promise(resolve => requestAnimationFrame(resolve))
    }
  }

  function resolveTargetPage(node: TocItemData): string | undefined {
    if (node.pageNumberString) {
      return node.pageNumberString
    }

    const normalizedHref = node.href?.split('#')[0]
    if (!normalizedHref) {
      return undefined
    }

    const chapterStart = flatTocItems.value.find(item =>
      !item.fragmentId
      && !!item.pageNumberString
      && item.href === normalizedHref,
    )

    return chapterStart?.pageNumberString
  }

  async function handleTocItemSelected(node: TocItemData) {
    const targetPageNumber = resolveTargetPage(node)
    const anchor = node.fragmentId || undefined

    if (!targetPageNumber) {
      onUnresolvedPageNumber?.(node)
      return
    }

    const chapterStartPageNumber = resolveChapterStartPage(node)
    const preloadPageNumber = resolveNextChapterPage(node)
    const alreadyLoaded = isTocNodePresentInLoadedContent(node)
    const shouldPreloadNextBatch = !alreadyLoaded
      && Boolean(anchor)
      && node.pageNumberString
      && chapterStartPageNumber
      && node.pageNumberString === chapterStartPageNumber
      && preloadPageNumber
      && preloadPageNumber !== targetPageNumber

    if (shouldPreloadNextBatch && preloadPageNumber) {
      updateUrl({
        pageNumber: preloadPageNumber,
        anchor: undefined,
      })

      await nextTick()
      await waitForFetchToSettle()
      await waitForPageToLoad(preloadPageNumber)
    }

    updateUrl({
      pageNumber: targetPageNumber,
      anchor,
    })
  }

  return {
    handleTocItemSelected,
  }
}
